import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { api } from '../lib/api';
import { toast } from 'react-toastify';
import Button from '../components/Button';

interface Listing {
  _id: string;
  name: string;
  title: string;
}

const ListingQRCodePage: React.FC = () => {
  const { id } = useParams();
  const [listing, setListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const fetchListing = useCallback(async () => {
    try {
      const data = await api.get<{ listing: Listing }>(`/listings/${id}`);
      setListing(data.listing);
    } catch {
      toast.error('Failed to load listing');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleDownload = () => {
    if (!listing || !qrCodeRef.current) return;

    const canvas = qrCodeRef.current.querySelector('canvas');
    if (!canvas) return;

    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Failed to generate QR code image');
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${listing.name}-qr-code.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('QR code downloaded successfully');
      });
    } catch {
      toast.error('Failed to download QR code');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!listing) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 mb-4">Listing not found</p>
        <Link to="/my-listings">
          <Button>Back to My Listings</Button>
        </Link>
      </div>
    );
  }

  const qrCodeUrl = `https://pay2rent.me/${listing.name}`;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <Link to="/my-listings" className="text-primary-600 hover:text-primary-700 text-sm">
          ← Back to My Listings
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">QR Code</h1>
        <h2 className="text-xl text-gray-700 mb-6">{listing.title}</h2>

        <div className="flex flex-col items-center">
          <div className="bg-white p-6 rounded-lg border-2 border-gray-200 mb-6" ref={qrCodeRef}>
            <QRCodeCanvas
              value={qrCodeUrl}
              size={256}
              level="M"
              includeMargin={true}
            />
          </div>

          <div className="text-center mb-6">
            <p className="text-sm text-gray-600 mb-1">QR Code URL:</p>
            <p className="text-sm font-mono text-gray-900 bg-gray-50 px-4 py-2 rounded">
              {qrCodeUrl}
            </p>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleDownload}>
              Download QR Code
            </Button>
            <Link to={`/listings/${listing._id}/edit`}>
              <Button variant="secondary">Edit Listing</Button>
            </Link>
          </div>

          <div className="mt-6 text-sm text-gray-500 text-center max-w-md">
            <p>
              Scan this QR code to access your listing directly. Share it on promotional materials,
              social media, or anywhere you want to make your listing easily accessible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingQRCodePage;

